import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  Renderer2,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { catchError, Observable, take, throwError, timer } from 'rxjs';

import { AuthService } from 'src/app/core/services/auth';
import { LoginProviderResponse } from 'src/app/core/services/auth/auth.types';
import { ToggleComponent } from 'src/app/shared/components/toggle/toggle.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { LoginStatusBannerComponent } from './login-status-banner/login-status-banner.component';

export function emailOrUsernameValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = (control.value || '').trim();
    if (!v) return { required: true };

    if (v.includes('@')) {
      const relaxedEmailRegex = /^[^\s@]+@[^\s@]+(\.[^\s@]+)?$/;
      return relaxedEmailRegex.test(v) ? null : { email: true };
    }

    return v.length >= 3 ? null : { usernameTooShort: true };
  };
}

@Component({
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    ToggleComponent,
    RouterLink,
    LoginStatusBannerComponent,
  ],
})
export class LoginComponent implements AfterViewInit {
  usernameValid: boolean = false;

  isLoading = signal(false);

  errorMessage = signal<string | null>(null);

  isLoading$: Observable<boolean>;

  errorMessage$: Observable<string>;

  messages = signal({
    usernameInput: '',
    emailInput: '',
    passwordInput: '',
  });

  currentStep = signal<'username' | 'password'>('username');

  submittedUsername = signal(false);

  submittedLogin = signal(false);

  usernameFormGroup: FormGroup<{
    username: FormControl<string>;
  }> = new FormGroup({
    username: new FormControl('', {
      nonNullable: true,
      validators: [emailOrUsernameValidator()],
    }),
  });

  loginFormGroup = new FormGroup({
    username: new FormControl('', {
      nonNullable: true,
      validators: [emailOrUsernameValidator()],
    }),
    password: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(8)],
    }),
    toggle: new FormControl(false, { nonNullable: true }),
  });

  userEmail = signal<string>('');

  private authService = inject(AuthService);

  private renderer = inject(Renderer2);

  ngAfterViewInit(): void {
    this.renderer.selectRootElement('#usernameId').focus();
  }

  login(): void {
    this.submittedLogin.set(true);

    if (this.loginFormGroup.invalid) {
      this.loginFormGroup.markAllAsTouched();

      const u = this.loginFormGroup.controls.username;
      const p = this.loginFormGroup.controls.password;

      if (u.errors) {
        if (u.errors.required)
          this.messages.update((m) => ({
            ...m,
            usernameInput: 'Please enter your username.',
          }));
        else if (u.errors.email)
          this.messages.update((m) => ({
            ...m,
            usernameInput: 'Please enter a valid email address.',
          }));
        else if (u.errors.usernameTooShort)
          this.messages.update((m) => ({
            ...m,
            usernameInput: 'Username must be at least 3 characters.',
          }));
        else this.messages.update((m) => ({ ...m, usernameInput: '' }));
      } else this.messages.update((m) => ({ ...m, usernameInput: '' }));

      if (p.errors) {
        if (p.errors.required)
          this.messages.update((m) => ({
            ...m,
            passwordInput: 'Please enter a password.',
          }));
        else if (p.errors.minlength)
          this.messages.update((m) => ({
            ...m,
            passwordInput: 'Password must be at least 8 characters.',
          }));
        else this.messages.update((m) => ({ ...m, passwordInput: '' }));
      } else this.messages.update((m) => ({ ...m, passwordInput: '' }));

      return;
    }
    const username = this.loginFormGroup.controls.username.value.trim();
    const password = this.loginFormGroup.controls.password.value;
    const rememberMe = this.loginFormGroup.controls.toggle.value;

    this.isLoading.set(true);

    this.authService
      .login(username, password, rememberMe)
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.isLoading.set(false);
          const invalid = 'Incorrect username or password.';
          if (error.status === 401)
            this.messages.update((m) => ({ ...m, passwordInput: invalid }));
          else if (error.status === 404)
            this.messages.update((m) => ({ ...m, usernameInput: invalid }));
          else
            this.errorMessage.set(
              "We can't log you in right now. Please try again later.",
            );
          this.clearErrorMessage();
          return throwError(() => error);
        }),
      )
      .subscribe(async (data) => {
        this.isLoading.set(false);
        if (
          data?.user?.OktaUsername__c ||
          data?.user?.Account?.Okta_Login_Enabled__c
        ) {
          await this.authService.loginWithOkta(data.user.OktaUsername__c);
        }
      });
  }

  checkLoginProvider(): void {
    this.submittedUsername.set(true);

    const username = this.usernameFormGroup.controls.username.value.trim();
    if (!username || this.usernameFormGroup.invalid) {
      this.messages.update((m) => ({
        ...m,
        usernameInput: 'Please enter your username.',
      }));
      return;
    }
    this.isLoading.set(true);
    this.messages.update((m) => ({ ...m, usernameInput: '' }));

    this.authService
      .checkLoginProvider(username)
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.messages.update((m) => ({
            ...m,
            usernameInput: 'Unable to verify username. Please try again later.',
          }));

          this.clearErrorMessage();
          return throwError(() => error);
        }),
      )
      .subscribe(async (data: LoginProviderResponse) => {
        this.isLoading.set(false);
        this.userEmail.set(this.usernameFormGroup.controls.username.value);

        if (data.type === 'IDP2') {
          await this.authService.loginWithOkta(username);
        } else {
          this.loginFormGroup.patchValue({
            username: this.usernameFormGroup.controls.username.value,
          });
          this.currentStep.set('password');
          setTimeout(() => {
            try {
              this.renderer.selectRootElement('#passwordId').focus();
            } catch {}
          });
        }
      });
  }

  goBack(): void {
    this.currentStep.set('username');
    this.renderer.selectRootElement('#usernameId').focus();
  }

  private clearErrorMessage(): void {
    timer(5000)
      .pipe(take(1))
      .subscribe(() => {
        this.errorMessage.set(null);
      });
  }
}
